import { authClient } from "@/lib/auth-client";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";

const searchSchema = z.object({
	token: z.string().optional(),
});

export const Route = createFileRoute("/verify-email")({
	component: VerifyEmailPage,
	validateSearch: searchSchema,
});

type Status = "pending" | "verifying" | "success" | "error" | "missing-token";

function VerifyEmailPage() {
	const { token } = Route.useSearch();
	const [status, setStatus] = useState<Status>("pending");
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	useEffect(() => {
		if (!token) {
			setStatus("missing-token");
			return;
		}

		let cancelled = false;
		setStatus("verifying");

		void authClient
			.verifyEmail({ query: { token } })
			.then((result) => {
				if (cancelled) return;
				if (result.error) {
					setErrorMessage(result.error.message ?? "Verification failed");
					setStatus("error");
				} else {
					setStatus("success");
				}
			})
			.catch((err: unknown) => {
				if (cancelled) return;
				setErrorMessage(err instanceof Error ? err.message : "Verification failed");
				setStatus("error");
			});

		return () => {
			cancelled = true;
		};
	}, [token]);

	return (
		<Shell title="Verify your email">
			{status === "verifying" && <p className="text-sm text-[#6b7280]">Verifying your email…</p>}

			{status === "success" && (
				<>
					<p className="text-sm text-[#1e1e20]">
						Your email is verified. You can now sign in to PayrollMart.
					</p>
					<Link
						to="/login"
						className="mt-4 inline-flex h-9 items-center justify-center rounded bg-[#16a34a] px-4 text-sm font-semibold text-white hover:bg-[#15803d]"
					>
						Continue to sign in
					</Link>
				</>
			)}

			{status === "error" && (
				<>
					<p className="text-sm text-[#dc2626]">{errorMessage ?? "Something went wrong."}</p>
					<p className="mt-3 text-sm text-[#6b7280]">
						The link may have expired. Sign in to request a new verification email.
					</p>
					<Link
						to="/login"
						className="mt-4 inline-flex h-9 items-center justify-center rounded border border-[#d1d5db] px-4 text-sm font-medium text-[#1e1e20] hover:bg-[#f3f4f6]"
					>
						Back to sign in
					</Link>
				</>
			)}

			{status === "missing-token" && (
				<>
					<p className="text-sm text-[#6b7280]">
						This page needs a verification token. Check the link in your email.
					</p>
					<Link
						to="/login"
						className="mt-4 inline-flex h-9 items-center justify-center rounded border border-[#d1d5db] px-4 text-sm font-medium text-[#1e1e20] hover:bg-[#f3f4f6]"
					>
						Back to sign in
					</Link>
				</>
			)}
		</Shell>
	);
}

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<div className="flex min-h-screen items-center justify-center bg-[#f8f9fb]">
			<div className="w-full max-w-sm space-y-4 rounded-lg border border-[#e5e7eb] bg-white p-8 shadow-sm">
				<div className="text-center">
					<div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-[#16a34a]">
						<span className="text-lg font-bold text-white">P</span>
					</div>
					<h1 className="text-xl font-semibold text-[#1e1e20]">{title}</h1>
				</div>
				<div className="flex flex-col">{children}</div>
			</div>
		</div>
	);
}
